'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { FilterState } from '@/app/(main)/(pages)/busca/[slug]/_types/filter-state.type';
import { SearchApiProcedure } from '@/app/(main)/(pages)/_types/search-api-response.type';
import { SearchFilterAgeGroupRangeMap } from '@/app/(main)/(pages)/busca/[slug]/_types/search-filter-age-group.type';
import { getSchedulesPreviewFromSearchApi } from '@/app/(main)/(pages)/busca/[slug]/_services/get-schedules-preview-from-search-api.service';
import { findProceduresBySlugs } from '@/app/(main)/(pages)/_services/search-api/search-api.service';
import { areFiltersEqual } from '@/app/(main)/(pages)/busca/[slug]/_utils/search-filter-params.util';
import { getAvailableFilters } from '@/app/(main)/(pages)/busca/[slug]/_utils/filter-availability.util';
import { SearchType } from '@/types/most-search-type';
import { useDebounceValue } from '@/hooks/use-debounce.hook';
import { useUserLocation } from '@/hooks/use-user-location.hook';

const PREVIEW_DEBOUNCE_MS = 400;

export interface EmptyPreviewResult {
  filters: FilterState;
  procedureCodes: string[];
}

interface UseFiltersPreviewParams {
  slugs: string[];
  draft: FilterState;
  applied: FilterState;
  modality: SearchType;
  enabled: boolean;
  onEmptyResult?: (result: EmptyPreviewResult) => void;
}

interface UseFiltersPreviewReturn {
  hasResults: boolean;
  isLoading: boolean;
  isError: boolean;
  retry: () => void;
}

export function useFiltersPreview({
  slugs,
  draft,
  applied,
  modality,
  enabled,
  onEmptyResult,
}: UseFiltersPreviewParams): UseFiltersPreviewReturn {
  const { userLocation } = useUserLocation();
  const debouncedDraft = useDebounceValue(draft, PREVIEW_DEBOUNCE_MS);

  const [hasResults, setHasResults] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const requestIdRef = useRef(0);
  const proceduresCacheRef = useRef<{ key: string; promise: Promise<SearchApiProcedure[]> } | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const retry = useCallback(() => setReloadToken((token) => token + 1), []);

  const slugsKey = slugs.join(',');
  const { latitude, longitude, city, state } = userLocation ?? {};
  const hasPendingChanges = !areFiltersEqual(debouncedDraft, applied);
  const { distance, ageGroup, dayTime, professionalGender, followUp, onlyScheduled, minDate, maxDate }
    = getAvailableFilters(debouncedDraft, modality);

  const resolveProcedures = useCallback((): Promise<SearchApiProcedure[]> => {
    if (proceduresCacheRef.current?.key === slugsKey) {
      return proceduresCacheRef.current.promise;
    }

    const promise = findProceduresBySlugs(slugs).catch((error) => {
      proceduresCacheRef.current = null;
      throw error;
    });

    proceduresCacheRef.current = { key: slugsKey, promise };
    return promise;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slugsKey]);

  const notifyEmptyResult = (procedureCodes: string[]): void => {
    if (!isMountedRef.current) {
      return;
    }

    onEmptyResult?.({ filters: debouncedDraft, procedureCodes });
  };

  useEffect(() => {
    if (!enabled || !hasPendingChanges) {
      setHasResults(true);
      setIsLoading(false);
      setIsError(false);
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    setIsLoading(true);
    setIsError(false);

    resolveProcedures()
      .then((procedures) => {
        if (procedures.length === 0) {
          if (requestId !== requestIdRef.current) {
            return null;
          }
          setHasResults(false);
          setIsLoading(false);
          return null;
        }

        const procedureCodes = procedures.map((procedure) => procedure.code);

        return getSchedulesPreviewFromSearchApi({
          procedureIds: procedures.map((procedure) => procedure.id),
          city,
          state,
          latitude,
          longitude,
          distance,
          dayTime,
          professionalGender,
          minDate,
          maxDate,
          followUp,
          onlyScheduled,
          ...(ageGroup ? SearchFilterAgeGroupRangeMap[ageGroup] : {}),
        }).then((hasSchedules) => ({ hasSchedules, procedureCodes }));
      })
      .then((outcome) => {
        if (outcome === null || requestId !== requestIdRef.current) {
          return;
        }
        setHasResults(outcome.hasSchedules);
        setIsLoading(false);

        if (!outcome.hasSchedules) {
          notifyEmptyResult(outcome.procedureCodes);
        }
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) {
          return;
        }
        setIsError(true);
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    hasPendingChanges,
    slugsKey,
    city,
    state,
    latitude,
    longitude,
    distance,
    ageGroup,
    dayTime,
    professionalGender,
    minDate,
    maxDate,
    followUp,
    onlyScheduled,
    reloadToken,
  ]);

  return { hasResults, isLoading, isError, retry };
}
